import { Alert, Box, Snackbar } from "@mui/material";
import { format } from "date-fns";
import { FormProvider } from "react-hook-form";
import { useFoilPeelTestForm } from "../../hooks/useFoilPeelTestForm";
import {
  LoaderFunctionArgs,
  useLoaderData,
  useRevalidator,
} from "react-router-dom";
import { DynamicTable, TableColumn } from "../../components/common/table/Table";
import { FoilPeelTestForm } from "../../components/common/form/FoilPeelForm";
import { FoilPeelTestFull } from "../../types/foilPeelTestTypes";
import { fetchFoilPeelTestsEnriched } from "../../services/foilPeelTestService";
import { useState } from "react";
import { CircularProgress } from "@mui/material";

export async function loader(_: LoaderFunctionArgs) {
  const stationId = 60;
  try {
    const lastFoilPeelTests = await fetchFoilPeelTestsEnriched();

    return {
      stationId,
      title: "Foil Peel Test",
      lastFoilPeelTests,
    };
  } catch (err) {
    console.error("FoilPeel loader failed:", err);
    return {
      stationId,
      lastFoilPeelTests: [],
    };
  }
}

export default function FoilPeelTestPage() {
  const methods = useFoilPeelTestForm();
  const loaderData = useLoaderData() as {
    stationId: number;
    lastFoilPeelTests: FoilPeelTestFull[];
    operatorOptions?: { label: string; value: string | number }[];
  };
  const rows: FoilPeelTestFull[] = Array.isArray(loaderData?.lastFoilPeelTests)
    ? loaderData.lastFoilPeelTests
    : [];

  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const revalidator = useRevalidator();
  const isReloading = revalidator.state === "loading";

  const handleSuccess = () => {
    setShowSuccess(true);
    revalidator.revalidate();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setShowSnackbar(true);
  };

  const handleSnackbarClose = (
    _: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShowSnackbar(false);
  };

  return (
    <Box p={4}>
      <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start">
        {/* Form Side */}
        <Box flex={1}>
          <FormProvider {...methods}>
            <FoilPeelTestForm
              stationId={loaderData.stationId}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </FormProvider>
        </Box>

        {/* Tables Side */}
        <Box flex={3} display="flex" flexDirection="column" gap={2}>
          {isReloading ? (
            <CircularProgress />
          ) : (
            <>
              <DynamicTable columns={columns} data={rows} />
              <DynamicTable columns={adhesionColumns} data={rows.slice(0, 3)} />
            </>
          )}
        </Box>
      </Box>

      {/* Feedback */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Eintrag erfolgreich gespeichert!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error || "Ein Fehler ist aufgetreten"}{" "}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const columns: TableColumn<FoilPeelTestFull>[] = [
  { key: "id", label: "ID" },
  {
    key: "dateInProduction",
    label: "Produktionsdatum",
    render: (v) =>
      typeof v === "string" || typeof v === "number"
        ? format(new Date(v), "yyyy-MM-dd HH:mm")
        : "-",
  },
  { key: "username", label: "QS" },
  { key: "testType", label: "Test Type" },
  { key: "testForArticle", label: "Test entspricht" },
  { key: "laminatorName", label: "Laminator" },
  { key: "laminatorRecipe", label: "Lami Programm" },
  { key: "frontGlassThickness", label: "Frontglas Dicke" },
  { key: "frontGlassBatchNumber", label: "Frontglas Charge Nr." },
  {
    key: "frontGlassManufacturingDate",
    label: "Frontglas Produktionsdatum",
    render: (v) =>
      typeof v === "string" || typeof v === "number"
        ? format(new Date(v), "yyyy-MM-dd")
        : "-",
  },
  { key: "evaFoilType", label: "EVA Typ" },
  { key: "evaFoilBatchNumber", label: "EVA Charge Nr." },
  { key: "backsheetType", label: "Backsheet Typ" },
  { key: "backsheetBatchNumber", label: "Backsheet Charge Nr." },
  {
    key: "isGlassCleaned",
    label: "Glas gereinigt?",
    render: (v) => (v ? "Ja" : "Nein"),
  },
  { key: "laminatorPosition", label: "Position auf dem Einfuhrtisch" },
];

const ADHESION_KEYS_EVA = [
  "G01",
  "G02",
  "G03",
  "G04",
  "G05",
  "G06",
  "G07",
] as const;
const ADHESION_KEYS_BACKSHEET = [
  "R01",
  "R02",
  "R03",
  "R04",
  "R05",
  "R06",
  "R07",
] as const;

const adhesionColumns: TableColumn<FoilPeelTestFull>[] = [
  { key: "id", label: "ID" },
  ...ADHESION_KEYS_EVA.map((k) => ({
    key: `adhesionEva_${k}` as any,
    label: k,
    render: (_v: unknown, row: FoilPeelTestFull) => row.adhesionEva?.[k] ?? "",
  })),
  ...ADHESION_KEYS_BACKSHEET.map((k) => ({
    key: `adhesionBacksheet_${k}` as any,
    label: k,
    render: (_v: unknown, row: FoilPeelTestFull) =>
      row.adhesionBacksheet?.[k] ?? "",
  })),
];
